/**
 * Utilitário de atribuição de origem do lead.
 *
 * Lê UTMs, gclid, fbclid e código de vendedor da URL atual. Se ausentes,
 * faz fallback para localStorage (first-touch) e sessionStorage (last-touch)
 * populados pelo tracker.js do site institucional (`nomoz.tech`).
 *
 * Como `app.nomoz.tech` e `nomoz.tech` são domínios diferentes, o storage
 * é independente; o tracker do institucional anexa os parâmetros na query
 * string dos links, e o app lê desses params e persiste localmente também.
 */

const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
];
const VENDOR_PARAMS = ['v', 'vendedor'];
const STORAGE_FIRST_TOUCH = 'nomoz_first_touch';
const STORAGE_LAST_TOUCH = 'nomoz_last_touch';
const FIRST_TOUCH_TTL_DAYS = 30;

function safeParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getParamsFromUrl() {
  const out = {};
  const params = new URLSearchParams(window.location.search);
  for (const key of TRACKED_PARAMS) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  const vendedor = params.get('v') || params.get('vendedor');
  if (vendedor) out.vendedor_codigo = vendedor;

  // first_touch serializado em base64 pelo tracker.js do institucional
  const ft = params.get('ft');
  if (ft) {
    try {
      out._ft_raw = JSON.parse(decodeURIComponent(escape(atob(ft))));
    } catch {
      /* ignora base64 malformado */
    }
  }
  return out;
}

function isExpired(captured_at) {
  if (!captured_at) return true;
  const t = new Date(captured_at).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t > FIRST_TOUCH_TTL_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Persiste atribuição no próprio domínio do app se veio via querystring.
 * Chamado no mount do Register.js para preservar entre recargas.
 */
export function persistAttributionFromUrl() {
  const fromUrl = getParamsFromUrl();
  const hasAny = Object.keys(fromUrl).length > 0;
  if (!hasAny) return;

  const now = new Date().toISOString();
  const payload = {
    ...fromUrl,
    referrer: document.referrer || null,
    landing_page: window.location.href,
    captured_at: now,
  };

  try {
    sessionStorage.setItem(STORAGE_LAST_TOUCH, JSON.stringify(payload));
  } catch {
    /* storage cheio */
  }

  try {
    const existing = safeParse(localStorage.getItem(STORAGE_FIRST_TOUCH));
    if (!existing || isExpired(existing.captured_at)) {
      // Preferir o _ft_raw (first-touch do institucional) se veio
      const firstTouch = fromUrl._ft_raw || payload;
      localStorage.setItem(STORAGE_FIRST_TOUCH, JSON.stringify(firstTouch));
    }
  } catch {
    /* storage desabilitado */
  }
}

/**
 * Retorna o pacote de atribuição consolidado para enviar ao backend
 * no payload de /auth/register.
 */
export function getAttribution() {
  const fromUrl = getParamsFromUrl();
  const hasUrl = Object.keys(fromUrl).filter(k => k !== '_ft_raw').length > 0;

  const lastTouch = safeParse(sessionStorage.getItem(STORAGE_LAST_TOUCH)) || {};
  const firstTouch = safeParse(localStorage.getItem(STORAGE_FIRST_TOUCH));

  // Last-touch: URL atual tem prioridade, senão sessionStorage
  const source = hasUrl ? { ...lastTouch, ...fromUrl } : lastTouch;

  const payload = {
    utm_source: source.utm_source || null,
    utm_medium: source.utm_medium || null,
    utm_campaign: source.utm_campaign || null,
    utm_term: source.utm_term || null,
    utm_content: source.utm_content || null,
    gclid: source.gclid || null,
    fbclid: source.fbclid || null,
    referrer: source.referrer || document.referrer || null,
    landing_page: source.landing_page || window.location.href,
    vendedor_codigo: source.vendedor_codigo || null,
    first_touch: firstTouch || source._ft_raw || null,
  };

  // Limpa chaves nulas para não poluir o payload
  Object.keys(payload).forEach(k => payload[k] == null && delete payload[k]);
  return payload;
}
